/**
 * Client for the SMS API.
 *
 * Every request carries the Cognito access token; the API verifies it and scopes
 * the response by role. A 401 means the session lapsed (re-authenticate); a 403 is
 * a genuine authorization refusal surfaced as its message.
 */

import type { AppConfig } from "./config";
import type { Identity } from "./auth";
import type {
  ProgramsResponse,
  ResetResult,
  SortKey,
  StudentDetail,
  StudentListResult,
  StudentSearchResult,
  WhoAmI,
} from "./types";

export class SessionExpired extends Error {}

async function request<T>(
  config: AppConfig,
  identity: Identity,
  path: string,
  method: "GET" | "POST" = "GET",
): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: { Authorization: `Bearer ${identity.accessToken}` },
    cache: "no-store",
  });
  if (response.status === 401) {
    throw new SessionExpired("Your session expired. Please sign in again.");
  }
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new Error(String(body.error ?? `Request failed (${response.status}).`));
  }
  return body as T;
}

function get<T>(config: AppConfig, identity: Identity, path: string): Promise<T> {
  return request<T>(config, identity, path);
}

export function whoami(config: AppConfig, identity: Identity): Promise<WhoAmI> {
  return get(config, identity, "/whoami");
}

export function programs(config: AppConfig, identity: Identity): Promise<ProgramsResponse> {
  return get(config, identity, "/programs");
}

export function me(config: AppConfig, identity: Identity): Promise<StudentDetail> {
  return get(config, identity, "/me");
}

export function getStudent(
  config: AppConfig,
  identity: Identity,
  studentId: string,
): Promise<StudentDetail> {
  return get(config, identity, `/students/${encodeURIComponent(studentId)}`);
}

export function listStudents(
  config: AppConfig,
  identity: Identity,
  program: string,
  sort: SortKey,
  cursor?: string | null,
): Promise<StudentListResult> {
  const params = new URLSearchParams({ program, sort });
  if (cursor) params.set("cursor", cursor);
  return get(config, identity, `/students?${params.toString()}`);
}

/**
 * Find students by name or student id.
 *
 * Deliberately not scoped to the selected program: a search box is most useful when
 * you do not already know which program the person is in. Pass `program` to narrow it.
 */
export function searchStudents(
  config: AppConfig,
  identity: Identity,
  query: string,
  program?: string | null,
): Promise<StudentSearchResult> {
  const params = new URLSearchParams({ q: query });
  if (program) params.set("program", program);
  return get(config, identity, `/students/search?${params.toString()}`);
}

/**
 * Restore the seeded baseline: advising notes and holds the demo added are undone.
 *
 * Staff only, enforced server side. Takes a few seconds — it is a table scan away —
 * so callers should show progress rather than assume it is instant.
 */
export function resetDemoData(config: AppConfig, identity: Identity): Promise<ResetResult> {
  return request(config, identity, "/admin/reset", "POST");
}
